import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppState } from '@api-ui-app/src/app/app.state';
import { ChooseTemplateModalFacade } from '@api-ui-app/src/app/choose-template-modal/choose-template-modal.facade';
import { ProductExpansionHistory } from '@api-ui-app/src/app/e-commerce-product-expansion/services/product-expansion-history';
import { GelatoPublisherEventName } from '@api-ui-app/src/app/e-commerce-product-expansion/types/gelato-publisher-event-name.enum';
import { ProductExpansionMode } from '@api-ui-app/src/app/e-commerce-product-expansion/types/product-expansion-mode';
import { ECommerceProductTemplatesGraphQlService } from '@api-ui-app/src/app/e-commerce-product-templates/services/e-commerce-product-templates-graph-ql.service';
import { EProductTemplateEventName } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-event-name.enum';
import { EProductTemplateUsageEventStep } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-usage-event-step.enum';
import { ECommerceStoreSelectionModalFacade } from '@api-ui-app/src/app/e-commerce-store-selection-modal/e-commerce-store-selection-modal.facade';
import { EProductExpansionEntryPoint } from '@api-ui-app/src/app/header-create-menu/types/upload-click-from.enum';
import { LoadStoresList, setSelectedStore } from '@api-ui-app/src/app/ngrx/e-commerce-stores.actions';
import { getActiveStores, getStoresList } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { AddStoreProductFlowService } from '@api-ui-app/src/app/shared/services/add-store-product-flow.service';
import { SubscriptionsFacade } from '@api-ui-app/src/app/subscriptions/+state/subscriptions.facade';
import { logEventV2 } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { EStore } from '@gelato-api-ui/core/e-commerce/e-store';
import { getDefaultStoresListRequest } from '@gelato-api-ui/core/e-commerce/helpers/getDefaultStoresListRequest';
import { GelatoPlusFeatures } from '@gelato-api-ui/sdk/src/lib/shared/subscriptions-shared/lib/gelato-plus-features';
import { GelatoPlusPopupLocations } from '@gelato-api-ui/sdk/src/lib/shared/subscriptions-shared/lib/gelato-plus-popup-locations';
import { GelatoSubscriptionModalType } from '@gelato-api-ui/sdk/src/lib/shared/subscriptions-shared/lib/gelato-subscription-modal';
import { GelatoPlusModalService } from '@gelato-api-ui/sdk/src/lib/shared/subscriptions-shared/services/gelato-plus-modal.service';
import { ShowGeneralErrorNotification } from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import * as actions from './+state/header-create-menu.actions';
import * as selectors from './+state/header-create-menu.selectors';

@Injectable()
export class HeaderCreateMenuFacade {
  readonly isGelatoUser$ = this.authService.isGelatoUser();
  readonly clientId$ = this.authService.getClientId();
  readonly stores$ = this.store.select(getStoresList);
  readonly gelatoPlusOrGoldIsActive$ = combineLatest([
    this.subscriptionsFacade.isPaidSubscriptionActive$,
    this.subscriptionsFacade.isGelatoPlusOrGoldTrialActive$,
  ]).pipe(
    map(
      ([gelatoPlusOrGoldIsActive, isGelatoPlusOrGoldTrialActive]) =>
        gelatoPlusOrGoldIsActive || isGelatoPlusOrGoldTrialActive,
    ),
  );
  readonly storesList$ = this.store.select(getStoresList);
  readonly activeStores$ = this.store.select(getActiveStores);

  readonly uploadedDesignsCount$ = this.store.select(selectors.getUploadedDesignsCount);

  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly addStoreProductFlowService: AddStoreProductFlowService,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly gelatoPlusModalService: GelatoPlusModalService,
    private readonly eCommerceStoreSelectionModalFacade: ECommerceStoreSelectionModalFacade,
    private readonly eCommerceProductTemplatesGraphQlService: ECommerceProductTemplatesGraphQlService,
    private readonly productExpansionHistory: ProductExpansionHistory,
    private readonly chooseTemplateModalFacade: ChooseTemplateModalFacade,
  ) {}

  loadStoresList() {
    this.clientId$
      .pipe(
        take(1),
        tap((clientId: string) => {
          this.store.dispatch(new LoadStoresList(getDefaultStoresListRequest(clientId), false));
          this.store.dispatch(actions.loadUploadDesignLeftLimits());
        }),
      )
      .subscribe();
  }

  placeAnOrder() {
    this.router.navigate(['/checkout/new']);
  }

  updateUploadDesignLeftLimitsMetadata(storeId: string) {
    this.store.dispatch(actions.updateUploadDesignLeftLimitsMetadata({ storeId }));
  }

  useTemplate(store?: EStore) {
    this.activeStores$.pipe(take(1)).subscribe(activeStores => {
      if (store?.id) {
        logEventV2(EProductTemplateEventName.USAGE_INITIATED, {
          step: EProductTemplateUsageEventStep.ADD_PRODUCT_BUTTON,
          ecomPlatform: store.type,
        });
        return this.chooseTemplateModalFacade.openModal({ storeId: store.id });
      }

      if (!activeStores.length) {
        this.router.navigate(['/templates/list']);
      }

      if (activeStores.length === 1) {
        const [firstStore] = activeStores;
        this.chooseTemplateModalFacade.openModal({ storeId: firstStore.id });

        logEventV2(EProductTemplateEventName.USAGE_INITIATED, {
          step: EProductTemplateUsageEventStep.ADD_PRODUCT_BUTTON,
          ecomPlatform: firstStore.type,
        });
      }

      if (activeStores.length > 1) {
        this.eCommerceStoreSelectionModalFacade.openModal({
          selectStoreAction: s => {
            this.chooseTemplateModalFacade.openModal({ storeId: s.id });
            logEventV2(EProductTemplateEventName.USAGE_INITIATED, {
              step: EProductTemplateUsageEventStep.ADD_PRODUCT_BUTTON,
              ecomPlatform: s.type,
            });
          },
        });
      }
    });
  }

  /*
   * Once store is provided init flow with this store,
   * otherwise select will be selected manually
   * */
  uploadDesign(store?: EStore, entryPoint?: EProductExpansionEntryPoint) {
    this.productExpansionHistory.setEntryPoint(entryPoint);
    this.productExpansionHistory.setNavigateFrom(this.router.url);
    combineLatest([this.activeStores$, this.uploadedDesignsCount$])
      .pipe(take(1))
      .subscribe(([activeStores, usage]) => {
        if (!store) {
          return this.initFlowBasedNumberOfActiveStore(activeStores, usage);
        }

        if (store) {
          logEventV2(GelatoPublisherEventName.UPLOAD_DESIGN_CLICKED, { ecomPlatform: store.type, usage });
          this.store.dispatch(setSelectedStore({ payload: store }));
          this.navigateToProductExpansionFlow();

          return;
        }
      });
  }

  initFlowBasedNumberOfActiveStore(activeStores: EStore[], usage: number): void {
    if (!activeStores.length) {
      return this.initFlowForProductTemplates();
    }

    if (activeStores.length > 1) {
      return this.eCommerceStoreSelectionModalFacade.openModal({
        selectStoreAction: () => this.router.navigate(['/product-expansion/init-upload-flow']),
      });
    }

    if (activeStores.length === 1) {
      const [store] = activeStores;
      this.navigateToProductExpansionFlow();
      this.store.dispatch(setSelectedStore({ payload: store }));

      logEventV2(GelatoPublisherEventName.UPLOAD_DESIGN_CLICKED, { ecomPlatform: store.type, usage });

      return;
    }
  }

  connectStore() {
    this.router.navigate(['/stores/list']);
  }

  showPlusSubscriptionModal(): void {
    this.gelatoPlusModalService.open(
      GelatoPlusFeatures.artworkUpload,
      GelatoPlusPopupLocations.homeScreen,
      GelatoSubscriptionModalType.GELATO_PLUS,
    );
  }

  navigateToProductExpansionFlow(): void {
    this.router.navigate(['/product-expansion'], {
      queryParams: {
        mode: ProductExpansionMode.Upload,
      },
    });
  }

  addNewProduct() {
    this.stores$
      .pipe(
        take(1),
        tap((stores: EStore[]) => {
          if (!stores?.length) {
            return this.router.navigate(['catalogue/templates']);
          }

          if (stores?.length === 1) {
            return this.addStoreProductFlowService.initiate(stores?.[0]);
          }

          if (stores?.length > 1) {
            this.router.navigate(['/stores/list']);
          }
        }),
      )
      .subscribe();
  }

  private setSelectedStore(store: EStore) {
    this.store.dispatch(setSelectedStore({ payload: store }));
  }

  private initFlowForProductTemplates() {
    this.clientId$
      .pipe(
        switchMap(
          (clientId: string): Observable<EStore> =>
            this.eCommerceProductTemplatesGraphQlService.fetchProductTemplatesStore(clientId),
        ),
        take(1),
        tap((store: EStore) => {
          this.setSelectedStore(store);
          this.navigateToProductExpansionFlow();
        }),
        catchError(() => {
          this.store.dispatch(new ShowGeneralErrorNotification());

          return of(null);
        }),
      )
      .subscribe();
  }
}
